import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import DOMPurify from 'dompurify';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, shareReplay, tap, finalize, take, takeWhile, mergeMap } from 'rxjs/operators';
import { ProfileActions } from 'src/app/@store/actions/profile.actions';
import { AppState } from 'src/app/@store/reducers';
import { ProfileSelectors } from 'src/app/@store/selectors/profile.selectors';
import { StsSelectors } from 'src/app/@store/selectors/sts.selectors';
import { addTargetBlankToAnchors } from 'src/app/plasma-ui-common/utils/add-target-blank-to-anchors';
import { AgreementService } from 'src/app/services/agreement/agreement.service';
import { I18nService } from 'src/app/services/i18n/i18n.service';
import { isInAvailableLocale } from 'src/app/transloco-root.module';

@Component({
    selector: 'app-managed-privacy-policy',
    templateUrl: './managed-privacy-policy.component.html',
    styleUrls: ['./managed-privacy-policy.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ManagedPrivacyPolicyComponent implements OnInit, OnDestroy {
  html$: Observable<any>;
  langCode$: Observable<any>;
  loading: boolean;
  private cookieScriptTimer: ReturnType<typeof setTimeout>;
  private destroyed = false;

  private readonly localeClaim$ = this.store.pipe(select(ProfileSelectors.getLocale));
  private readonly isLoggedIn$ = this.store.pipe(select(StsSelectors.isLoggedIn));
  private readonly isItaly$ = this.store.pipe(select(StsSelectors.isITTenant));
  firstLoad$ = new BehaviorSubject(true);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly transloco: TranslocoService,
    private readonly i18n: I18nService,
    private readonly agreement: AgreementService,
    private readonly sanitizer: DomSanitizer,
    private readonly changeRef: ChangeDetectorRef,
    private readonly store: Store<AppState>,
  ) {}

  detectChanges(fn: () => void) {
    fn();
    this.changeRef.detectChanges();
  }

  ngOnInit() {
    this.isLoggedIn$.pipe(take(1)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        // load profile to get "locale" claims
        this.store.dispatch(ProfileActions.getProfile());
        this.isLoggedIn();
      } else {
        this.isLoggedOut();
      }
    });
  }

  isLoggedIn() {
    this.loading = false;
    this.langCode$ = combineLatest([
      this.localeClaim$,
      this.agreement.getLocales(),
      this.route.queryParamMap.pipe(map(x => x.get('langCode'))),
      this.transloco.langChanges$,
    ]).pipe(
      map(([userLocale, locales, qsLang, translocoLang]) => {
        const l = qsLang && this.firstLoad$.getValue() ? qsLang : translocoLang;
        const activeLang = userLocale && this.firstLoad$.getValue() ? userLocale : l;
        this.firstLoad$.next(false);
        if (activeLang) {
          return this.i18n.getPreferredSupportedLanguage(
            [activeLang],
            locales.map(l => l.cultureCode),
          );
        } else {
          return '';
        }
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const type = this.route.snapshot.data['page'];
    this.html$ = combineLatest([of(type), this.langCode$]).pipe(
      tap(() => this.detectChanges(() => (this.loading = true))),
      mergeMap(([type, lang]) => this.agreement.getPublishedAgreementByLocale(type, lang)),
      map(x => x.files[0]),
      map(x => (x ? x.htmlFile : '')),
      map(html => {
        const purified = DOMPurify.sanitize(html, {
          ADD_TAGS: ['link'],
          ADD_ATTR: ['href', 'rel'],
          FORCE_BODY: true,
        });
        this.loading = false;
        return this.sanitizer.bypassSecurityTrustHtml(addTargetBlankToAnchors(purified));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
      tap(() =>
        this.detectChanges(() => { this.loading = false; }),
      ),
      finalize(() => {
        this.detectChanges(() => {
          this.loading = false;
          this.scheduleCookieScriptForIT();
        });
      }),
    );
  }

  isLoggedOut() {
    this.loading = false;
    this.langCode$ = combineLatest([
      this.agreement.getLocales(),
      this.route.queryParamMap.pipe(map(x => x.get('langCode'))),
      this.transloco.langChanges$,
    ]).pipe(
      map(([locales, qsLang, translocoLang]) => {
        const activeLang = qsLang && this.firstLoad$.getValue() ? qsLang : translocoLang;
        if (activeLang) {
          if (this.firstLoad$.getValue()) {
            this.firstLoad$.next(false);
            const inAvailableLang = isInAvailableLocale(
              activeLang,
              this.transloco.getAvailableLangs() as string[],
            );
            if (inAvailableLang != '') {
              this.transloco.setActiveLang(inAvailableLang);
            }
          }
          return this.i18n.getPreferredSupportedLanguage(
            [activeLang],
            locales.map(l => l.cultureCode),
          );
        } else {
          return '';
        }
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const type = this.route.snapshot.data['page'];
    this.html$ = combineLatest([of(type), this.langCode$]).pipe(
      takeWhile(() => !this.firstLoad$.getValue()),
      tap(() => this.detectChanges(() => (this.loading = true))),
      mergeMap(([type, lang]) => this.agreement.getPublishedAgreementByLocale(type, lang)),
      map(x => x.files[0]),
      map(x => (x ? x.htmlFile : '')),
      map(html => {
        const purified = DOMPurify.sanitize(html, {
          ADD_TAGS: ['link'],
          ADD_ATTR: ['href', 'rel'],
          FORCE_BODY: true,
        });
        return this.sanitizer.bypassSecurityTrustHtml(addTargetBlankToAnchors(purified));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
      tap(() =>
        this.detectChanges(() => { this.loading = false; }),
      ),
      finalize(() =>
        this.detectChanges(() => {
          this.loading = false;
          this.scheduleCookieScriptForIT();
        }),
      ),
    );
  }

  /**
   * `finalize` also runs when the last subscriber goes away — which, since the template
   * reads `html$` through an async pipe, means when the view is destroyed. Injecting
   * OneTrust 150ms after that is pointless in the browser, and in a spec it lands after
   * the jsdom environment is gone: an unhandled `ReferenceError: document is not defined`
   * that fails the whole run with every test passing (build 1.0.97-1078). So the handle is
   * kept and cancelled on destroy.
   */
  private scheduleCookieScriptForIT() {
    this.cookieScriptTimer = setTimeout(() => this.insertCookieScriptForIT(), 150);
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.cookieScriptTimer);
  }

  async insertCookieScriptForIT() {
    const isItaly = await firstValueFrom(this.isItaly$);

    // `destroyed` is re-checked here because the await above can resolve after the timer
    // was cancelled — clearing the handle cannot stop a call already in flight.
    if (!isItaly || this.destroyed) return;

    const COOKIE_SCRIPT_ID = '01993408-1506-7c9d-94d3-6a593e8aceb5';

    this.addScript({
      src: 'https://cdn.cookielaw.org/consent/' + COOKIE_SCRIPT_ID + '/OtAutoBlock.js',
    });
    this.addScript({
      src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
      attrs: {
        charset: 'UTF-8',
        'data-domain-script': COOKIE_SCRIPT_ID,
      },
    });
    this.addScript({ src: null, text: 'function OptanonWrapper() { /* your code here */ }' });
  }

  addScript({
    src,
    text = '',
    attrs = {},
  }: {
    src?: string;
    text?: string;
    attrs?: Record<string, string>;
  }) {
    const NONCE = 'hY7p2Qw8XzMv3K9B';

    // Check if a script with the same `src` already exists
    if (src && document.querySelector(`script[src="${src}"]`)) {
      console.log(`Script with src "${src}" already exists. Skipping.`);
      return;
    }

    // Check if a script with the same inline text already exists
    if (!src && text) {
      const existingScripts = Array.from(document.querySelectorAll('script'));
      if (existingScripts.some(script => script.text === text)) {
        console.log('Inline script with same content already exists. Skipping.');
        return;
      }
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.setAttribute('nonce', NONCE);

    if (src) script.src = src;
    if (text) script.text = text;

    for (const [key, value] of Object.entries(attrs)) {
      script.setAttribute(key, value);
    }

    document.head.appendChild(script);
  }
}
